// 顧客情報管理用のユーティリティ（theme.jsから分離）
// 独自のSHOP_DOMAIN、STOREFRONT_TOKEN、GraphQL.fetchを定義

const Customer = {
    idToGid: (id) => {
        return `gid://shopify/Customer/${id}`;
    },

    getById: async (id, customerAccessToken) => {
        const query = `query getCustomer($customerAccessToken: String!) {
      customer(customerAccessToken: $customerAccessToken) {
        id
        email
        firstName
        lastName
        phone
        acceptsMarketing
        defaultAddress {
          id
          address1
          address2
          city
          province
          country
          zip
          firstName
          lastName
          phone
        }
        addresses(first: 20) {
          nodes {
            id
            address1
            address2
            city
            province
            country
            zip
            firstName
            lastName
            phone
          }
        }
      }
    }`;
        const variables = { customerAccessToken };
        const res = await GraphQL.fetch(query, variables);
        return res?.customer || null;
    },

    updateById: async (id, customerAccessToken, customer) => {
        const mutation = `mutation customerUpdate($customerAccessToken: String!, $customer: CustomerUpdateInput!) {
      customerUpdate(customerAccessToken: $customerAccessToken, customer: $customer) {
        customer {
          id
          email
          firstName
          lastName
          phone
          acceptsMarketing
        }
        customerUserErrors {
          code
          field
          message
        }
      }
    }`;
        const variables = { customerAccessToken, customer };
        const res = await GraphQL.fetch(mutation, variables);
        if (res?.customerUpdate) return res.customerUpdate;
        const errs = (window.__lastGraphQLErrors || []).map(e => ({ message: e?.message || 'エラーが発生しました。' }));
        return { customerUserErrors: errs.length ? errs : [{ message: '接続エラー。後でもう一度お試しください。' }] };
    },

    updateAddress: async (addressId, customerAccessToken, address) => {
        // Shopify Storefront API仕様に従って住所入力を準備
        const addressInput = {};
        const addressFields = ['firstName', 'lastName', 'address1', 'address2', 'city', 'province', 'zip', 'phone', 'company'];

        addressFields.forEach(field => {
            const value = address[field];
            if (value && typeof value === 'string' && value.trim()) {
                addressInput[field] = value.trim();
            }
        });

        // 国フィールドの特別処理（"Japan" -> "JP"に変換）
        const countryValue = address.country === 'Japan' ? 'JP' : (address.country || 'JP');
        if (countryValue && countryValue.trim()) {
            addressInput.country = countryValue.trim();
        }
        
        const mutation = `mutation customerAddressUpdate($customerAccessToken: String!, $id: ID!, $address: MailingAddressInput!) {
      customerAddressUpdate(customerAccessToken: $customerAccessToken, id: $id, address: $address) {
        customerAddress {
          id
          firstName
          lastName
          phone
          address1
          address2
          city
          province
          country
          countryCodeV2
          zip
          company
        }
        customerUserErrors {
          code
          field
          message
        }
      }
    }`;
        const variables = { customerAccessToken, id: addressId, address: addressInput };
        const res = await GraphQL.fetch(mutation, variables);
        if (res?.customerAddressUpdate) return res.customerAddressUpdate;
        const errs = (window.__lastGraphQLErrors || []).map(e => ({ message: e?.message || 'エラーが発生しました。' }));
        return { customerUserErrors: errs.length ? errs : [{ message: '接続エラー。後でもう一度お試しください。' }] };
    },

    createAddress: async (customerAccessToken, address) => {
        // Shopify Storefront API仕様に従って住所入力を準備
        const addressInput = {};
        const addressFields = ['firstName', 'lastName', 'address1', 'address2', 'city', 'province', 'zip', 'phone', 'company'];

        addressFields.forEach(field => {
            const value = address[field];
            if (value && typeof value === 'string' && value.trim()) {
                addressInput[field] = value.trim();
            }
        });

        // 国フィールドの特別処理（"Japan" -> "JP"に変換）
        const countryValue = address.country === 'Japan' ? 'JP' : (address.country || 'JP');
        if (countryValue && countryValue.trim()) {
            addressInput.country = countryValue.trim();
        }
        
        const mutation = `mutation customerAddressCreate($customerAccessToken: String!, $address: MailingAddressInput!) {
      customerAddressCreate(customerAccessToken: $customerAccessToken, address: $address) {
        customerAddress { 
          id
          firstName
          lastName
          phone
          address1
          address2
          city
          province
          country
          countryCodeV2
          zip
          company
        }
        customerUserErrors { code field message }
      }
    }`;
        const variables = { customerAccessToken, address: addressInput };
        const res = await GraphQL.fetch(mutation, variables);
        if (res?.customerAddressCreate) return res.customerAddressCreate;
        const errs = (window.__lastGraphQLErrors || []).map(e => ({ message: e?.message || 'エラーが発生しました。' }));
        return { customerUserErrors: errs.length ? errs : [{ message: '接続エラー。後でもう一度お試しください。' }] };
    },

    deleteAddress: async (addressId, customerAccessToken) => {
        const mutation = `mutation customerAddressDelete($customerAccessToken: String!, $id: ID!) {
      customerAddressDelete(customerAccessToken: $customerAccessToken, id: $id) {
        deletedCustomerAddressId
        customerUserErrors { code field message }
      }
    }`;
        const variables = { customerAccessToken, id: addressId };
        const res = await GraphQL.fetch(mutation, variables);
        if (res?.customerAddressDelete) return res.customerAddressDelete;
        const errs = (window.__lastGraphQLErrors || []).map(e => ({ message: e?.message || 'エラーが発生しました。' }));
        return { customerUserErrors: errs.length ? errs : [{ message: '接続エラー。後でもう一度お試しください。' }] };
    },
};

const CustomerAuth = {
    create: async (email, password) => {
        const mutation = `mutation customerAccessTokenCreate($input: CustomerAccessTokenCreateInput!) {
      customerAccessTokenCreate(input: $input) {
        customerAccessToken { accessToken expiresAt }
        customerUserErrors { code field message }
      }
    }`;
        const variables = { input: { email, password } };
        const res = await GraphQL.fetch(mutation, variables);
        return res?.customerAccessTokenCreate || null;
    },

    renew: async (customerAccessToken) => {
        const mutation = `mutation customerAccessTokenRenew($customerAccessToken: String!) {
      customerAccessTokenRenew(customerAccessToken: $customerAccessToken) {
        customerAccessToken { accessToken expiresAt }
        userErrors { field message }
      }
    }`;
        const variables = { customerAccessToken };
        const res = await GraphQL.fetch(mutation, variables);
        return res?.customerAccessTokenRenew || null;
    },

    delete: async (customerAccessToken) => {
        const mutation = `mutation customerAccessTokenDelete($customerAccessToken: String!) {
      customerAccessTokenDelete(customerAccessToken: $customerAccessToken) {
        deletedAccessToken
        deletedCustomerAccessTokenId
        userErrors { field message }
      }
    }`;
        const variables = { customerAccessToken };
        const res = await GraphQL.fetch(mutation, variables);
        return res?.customerAccessTokenDelete || null;
    },

    saveToStorage: (accessToken, expiresAt) => {
        try {
            localStorage.setItem('customerAccessToken', accessToken);
            localStorage.setItem('customerAccessTokenExpiresAt', expiresAt);
        } catch (e) { }
    },

    loadFromStorage: () => {
        try {
            const accessToken = localStorage.getItem('customerAccessToken');
            const expiresAt = localStorage.getItem('customerAccessTokenExpiresAt');
            return accessToken ? { accessToken, expiresAt } : null;
        } catch (e) { return null; }
    },

    clearStorage: () => {
        try {
            localStorage.removeItem('customerAccessToken');
            localStorage.removeItem('customerAccessTokenExpiresAt');
        } catch (e) { }
    },
};

// 顧客アクセストークン作成の便利関数
const createCustomerAccessToken = async (email, password) => {
    const mutation = `mutation customerAccessTokenCreate($input: CustomerAccessTokenCreateInput!) {
    customerAccessTokenCreate(input: $input) {
      customerAccessToken { accessToken expiresAt }
      customerUserErrors { code field message }
    }
  }`;
    const variables = { input: { email, password } };
    const res = await GraphQL.fetch(mutation, variables);
    return res?.customerAccessTokenCreate || null;
};

// グローバルに公開（テンプレートやコンソールで使用可能）
window.Customer = Customer;
window.CustomerAuth = CustomerAuth;
window.createCustomerAccessToken = createCustomerAccessToken;

// "contact_form"フォームのバリデーター初期化
if (typeof ItaijiValidator !== "undefined") {
    ItaijiValidator.init("#contact_form", [
        { input: "#first_name", parent: "contact_form" },
        { input: "#last_name", parent: "contact_form" },
        { input: "#email", parent: "create_customer" },
        { input: "#password", parent: "create_customer" }
    ]);
}


// 顧客情報と住所の保存処理（Storefront API）
(function () {
    // 同時保存操作を防ぐフラグ
    let isSaving = false;

    // ヘルパー関数
    function getTrimmedValue(selector, $scope) {
        const $ctx = $scope || $(document);
        return $.trim($ctx.find(selector).val() || '');
    }

    function getBooleanChecked(selector) {
        return !!$(selector).prop('checked');
    }

    // 住所ラッパーから住所データを読み取り
    function readAddressFromWrapper($w) {
        const pick = (selectors) => {
            for (let i = 0; i < selectors.length; i++) {
                const val = getTrimmedValue(selectors[i], $w);
                if (val) return val;
            }
            return '';
        };

        // 都道府県の特別処理：customer[addresses][id][province]を優先
        const getProvince = () => {
            const $provinceInputs = $w.find('input[name*="[province]"]');
            for (let i = 0; i < $provinceInputs.length; i++) {
                const $input = $($provinceInputs[i]);
                const name = $input.attr('name') || '';
                if (name.includes('addresses') && name.includes('[province]')) {
                    const val = $.trim($input.val() || '');
                    if (val) return val;
                }
            }
            return pick([
                'input[name="address[province]"]',
                'input[name$="[province]"]',
                'input[name="customer[province]"]',
            ]);
        };

        const address = {
            firstName: pick([
                'input[name="address[first_name]"]',
                'input[name="customer[first_name]"]',
            ]),
            lastName: pick([
                'input[name="address[last_name]"]',
                'input[name="customer[last_name]"]',
            ]),
            phone: pick([
                'input[name="address[phone]"]',
                'input[name$="[phone]"]',
                'input[name="customer[phone]"]',
            ]),
            address1: pick([
                'input[name="address[address1]"]',
                'input[name$="[address1]"]',
                'input[name="customer[address]"]',
            ]),
            address2: pick([
                'input[name="address[address2]"]',
                'input[name$="[address2]"]',
                'input[name="customer[detail]"]',
            ]),
            city: pick([
                'input[name="address[city]"]',
                'input[name$="[city]"]',
            ]),
            province: getProvince(),
            country: pick([
                'input[name="address[country]"]',
            ]) || 'JP', // Use ISO country code (JP for Japan)
            zip: pick([
                'input[name="address[zip]"]',
                'input[name$="[zip]"]',
                'input[name="customer[code]"]',
            ]),
        };

        Object.keys(address).forEach(function (k) {
            if (address[k] === '') delete address[k];
        });

        // 電話番号をE.164形式に正規化（Shopify API用、入力フィールドの元の値は保持）
        if (address.phone) {
            let phone = String(address.phone).trim();
            phone = phone.replace(/[^\d+]/g, '');
            if (phone && !phone.startsWith('+')) {
                if (phone.startsWith('0')) {
                    address.phone = '+81' + phone.substring(1);
                } else {
                    address.phone = '+81' + phone;
                }
            } else {
                address.phone = phone;
            }
        }

        return address;
    }

    // 初期化：Storefront GIDを住所ラッパーにマッピング
    $(async function () {
        try {
            if (!window.CustomerAuth || !window.Customer || !window.Customer.getById) return;
            const stored = window.CustomerAuth.loadFromStorage && window.CustomerAuth.loadFromStorage();
            const token = stored && stored.accessToken;
            if (!token) return;
            const me = await window.Customer.getById(null, token);
            const nodes = me && me.addresses && me.addresses.nodes ? me.addresses.nodes : [];
            $('.address_wrapper').each(function (i) {
                const node = nodes[i];
                if (node && node.id) {
                    $(this).attr('data-id', node.id);
                }
            });
        } catch (_) { }
    });

    // 初期化：サーバー提供のIDがあれば使用
    $('.address_wrapper').each(function () {
        const $w = $(this);
        const existing = $w.attr('data-id');
        if (existing && typeof existing === 'string' && existing.indexOf('gid://shopify/') === 0) {
            return;
        }
        const realId = $w.find('input[name="address[id]"]').val();
        if (realId && realId.indexOf('gid://shopify/') === 0) {
            $w.attr('data-id', realId);
        }
    });

    // 最後のbtn_address要素にbtn_lastクラスを追加
    function updateBtnLastClass() {
        $('.btn_address').removeClass('btn_last');
        const $lastWrapper = $('.address_wrapper:not(.deleted)').last();
        if ($lastWrapper.length > 0) {
            $lastWrapper.find('.btn_address').addClass('btn_last');
        }
    }

    $(document).ready(function () {
        updateBtnLastClass();
    });

    window.updateBtnLastClass = updateBtnLastClass;


    // 有効なアクセストークンを取得（更新サポート付き）
    async function getValidAccessToken() {
        try {
            if (!window.CustomerAuth) return null;
            const stored = window.CustomerAuth.loadFromStorage && window.CustomerAuth.loadFromStorage();
            let token = stored && stored.accessToken;
            if (!token) return null;

            if (window.Customer && window.Customer.getById) {
                const me = await window.Customer.getById(null, token);
                if (me && me.id) return token;
            }

            if (window.CustomerAuth.renew) {
                const renewed = await window.CustomerAuth.renew(token);
                const newTok = renewed && renewed.customerAccessToken && renewed.customerAccessToken.accessToken;
                const expAt = renewed && renewed.customerAccessToken && renewed.customerAccessToken.expiresAt;
                if (newTok) {
                    window.CustomerAuth.saveToStorage && window.CustomerAuth.saveToStorage(newTok, expAt);
                    return newTok;
                }
            }
            return null;
        } catch (err) {
            return null;
        }
    }

    // アカウント編集フォームの送信を防止（リカバーフォームは除く）
    $(document).on('submit', 'form', function (e) {
        const $form = $(this);
        const $submitBtn = $form.find('.btn_account');
        const isRecoverForm = $form.find('input[name="form_type"]').val() === 'recover_customer_password' ||
            $form.attr('action') && $form.attr('action').includes('/account/recover');
        if ($submitBtn.length > 0 && !isRecoverForm) {
            e.preventDefault();
            e.stopPropagation();
            return false;
        }
    });

    // メイン保存ハンドラー
    $(document).on('click', '.btn_account', async function (e) {
        e.preventDefault();
        e.stopPropagation();

        if (isSaving) return;

        $('.txt_error, #txt_error').hide().text('');

        const $inputs = $('form:visible').find('input');
        $inputs.trigger('blur');

        const $errorInputs = $inputs.filter('.error');
        if ($errorInputs.length > 0) {
            const $firstError = $errorInputs.first();
            $('html, body').animate({
                scrollTop: $firstError.offset().top - 100
            }, 500);
            alert('入力内容を確認してください。');
            return;
        }

        isSaving = true;
        const $btn = $(this);
        const originalText = $btn.text();
        $btn.prop('disabled', true).text('保存中...');

        let customerAccessToken = await getValidAccessToken();

        if (!customerAccessToken || !window.Customer) {
            isSaving = false;
            $btn.prop('disabled', false).text(originalText);
            alert('ログインが必要です。もう一度ログインしてください。');
            return;
        }

        const customerPayload = {};
        const email = getTrimmedValue('input[name="customer[email]"]');
        const firstName = getTrimmedValue('input[name="customer[first_name]"]');
        const lastName = getTrimmedValue('input[name="customer[last_name]"]');
        const phone = getTrimmedValue('input[name="customer[phone]"]');
        const acceptsMarketing = getBooleanChecked('input[name="customer[accepts_marketing]"]');

        // 電話番号をE.164形式に正規化
        let normalizedPhone = phone;
        if (phone && phone.trim()) {
            let phoneValue = phone.trim();
            phoneValue = phoneValue.replace(/[^\d+]/g, '');
            if (phoneValue && !phoneValue.startsWith('+')) {
                if (phoneValue.startsWith('0')) {
                    normalizedPhone = '+81' + phoneValue.substring(1);
                } else {
                    normalizedPhone = '+81' + phoneValue;
                }
            } else {
                normalizedPhone = phoneValue;
            }
        }

        if (email) customerPayload.email = email;
        if (firstName) customerPayload.firstName = firstName;
        if (lastName) customerPayload.lastName = lastName;
        if (normalizedPhone) customerPayload.phone = normalizedPhone;
        customerPayload.acceptsMarketing = acceptsMarketing;

        let hadError = false;
        const messages = [];

        // 1) 顧客基本情報の更新
        try {
            const updateRes = await window.Customer.updateById(null, customerAccessToken, customerPayload);
            if (updateRes && updateRes.customerUserErrors && updateRes.customerUserErrors.length) {
                hadError = true;
                messages.push(updateRes.customerUserErrors.map(u => u.message).join('\n'));
            }
        } catch (err) {
            hadError = true;
            messages.push('顧客情報の更新に失敗しました。');
        }

        // 1.5) パスワードバリデーション（パスワードフィールドが提供されている場合）
        let newPassword = getTrimmedValue('input[name="customer[password]"]') || getTrimmedValue('#password');
        let confirmPassword = getTrimmedValue('input[name="customer[confirm_password]"]') || getTrimmedValue('#confirm_password');

        let shouldRedirectToRecover = false;
        if (newPassword || confirmPassword) {
            if (!newPassword) {
                hadError = true;
                messages.push('パスワードを入力してください。');
            } else if (!confirmPassword) {
                hadError = true;
                messages.push('パスワード再確認を入力してください。');
            } else if (newPassword !== confirmPassword) {
                hadError = true;
                const errorMsg = 'パスワードとパスワード再確認が一致しません。';
                messages.push(errorMsg);

                let $txtError = $('.txt_error, #txt_error');
                if ($txtError.length === 0) {
                    $txtError = $('<p class="txt_error"></p>');
                    const $form = $('#form_account');
                    if ($form.length > 0) {
                        $form.prepend($txtError);
                    } else {
                        $('.btn_account').before($txtError);
                    }
                }
                $txtError.text(errorMsg).show();
            } else {
                shouldRedirectToRecover = true;
            }
        }

        // 2) 削除マークされた住所を削除
        const deletedAddressIds = window.deletedAddressIds || [];
        if (deletedAddressIds.length > 0) {
            for (let i = 0; i < deletedAddressIds.length; i++) {
                const addressId = deletedAddressIds[i];
                if (addressId && addressId.indexOf('gid://shopify/') === 0) {
                    try {
                        const deleteRes = await window.Customer.deleteAddress(addressId, customerAccessToken);
                        if (deleteRes && deleteRes.customerUserErrors && deleteRes.customerUserErrors.length) {
                            hadError = true;
                            messages.push(deleteRes.customerUserErrors.map(u => u.message).join('\n'));
                        }
                    } catch (err) {
                        hadError = true;
                        messages.push('住所の削除に失敗しました。');
                    }
                }
            }
            window.deletedAddressIds = [];
        }

        // 3) すべての表示中のラッパーから住所を作成/更新（削除されたものを除く）
        const $wrappers = $('.address_wrapper:not(.deleted)');
        for (let i = 0; i < $wrappers.length; i++) {
            const $w = $($wrappers[i]);
            const addressInput = readAddressFromWrapper($w);
            if (!addressInput || Object.keys(addressInput).length === 0) continue;

            let gid = $w.attr('data-id');
            if (gid && gid.indexOf('gid://shopify/') !== 0) {
                gid = null;
            }

            try {
                if (gid) {
                    const res = await window.Customer.updateAddress(gid, customerAccessToken, addressInput);
                    if (res && res.customerUserErrors && res.customerUserErrors.length) {
                        hadError = true;
                        messages.push(res.customerUserErrors.map(u => u.message).join('\n'));
                    }
                } else {
                    const res = await window.Customer.createAddress(customerAccessToken, addressInput);
                    if (res && res.customerUserErrors && res.customerUserErrors.length) {
                        hadError = true;
                        messages.push(res.customerUserErrors.map(u => u.message).join('\n'));
                    } else if (res && res.customerAddress && res.customerAddress.id) {
                        $w.attr('data-id', res.customerAddress.id);
                    }
                }
            } catch (err) {
                hadError = true;
                messages.push('住所の更新に失敗しました。');
            }
        }

        // 結果を表示
        if (hadError) {
            isSaving = false;
            $btn.prop('disabled', false).text(originalText);
            alert(messages.filter(Boolean).join('\n'));
        } else {
            if (shouldRedirectToRecover) {
                alert('保存しました。パスワードリセットページに移動します。');
                window.location.href = '/account/login#recover';
                return;
            }

            // リロード前に元の電話番号と都道府県の値を保存
            const originalValues = {};
            $('input[type="tel"], input[id*="phone"]').each(function () {
                const $input = $(this);
                const name = $input.attr('name') || $input.attr('id');
                originalValues[name] = $input.val();
            });
            $('input[name*="[province]"], input[name="customer[province]"]').each(function () {
                const $input = $(this);
                const name = $input.attr('name') || $input.attr('id');
                originalValues[name] = $input.val();
            });

            try {
                sessionStorage.setItem('originalFormValues', JSON.stringify(originalValues));
            } catch (e) { }

            alert('保存しました。');
            window.location.reload();
        }
    });
})();